import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { FaArrowLeft, FaFilePdf, FaCalendarAlt, FaUser, FaChild } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function AssessmentsPage() {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAssessments = async () => {
      try {
        if (!auth.currentUser) {
          setError('Please sign in to view your assessments');
          setLoading(false);
          return;
        }

        const q = query(
          collection(db, 'assessments'),
          where('userId', '==', auth.currentUser.uid),
          orderBy('createdAt', 'desc')
        );

        const querySnapshot = await getDocs(q);
        const assessmentsData = [];
        
        querySnapshot.forEach((doc) => {
          assessmentsData.push({
            id: doc.id,
            ...doc.data(),
            // Convert Firestore timestamp to Date
            createdAt: doc.data().createdAt?.toDate() || new Date(),
          });
        });

        setAssessments(assessmentsData);
      } catch (err) {
        console.error('Error fetching assessments:', err);
        setError('Failed to load assessments. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAssessments();
  }, []);

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getRiskLevelClass = (level) => {
    switch(level?.toLowerCase()) {
      case 'high risk':
        return 'bg-red-100 text-red-800';
      case 'medium risk':
        return 'bg-yellow-100 text-yellow-800';
      case 'low risk':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white">
        <Header />
        <main className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your assessments...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
          >
            <FaArrowLeft className="mr-2" /> Back
          </button>
          
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900">My Assessments</h1>
            <button
              onClick={() => navigate('/free-assessment')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              New Assessment
            </button>
          </div>
          
          {error && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
              <p>{error}</p>
            </div>
          )}
          
          {assessments.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="text-gray-400 mb-4">
                <FaFilePdf className="text-5xl mx-auto opacity-50" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No assessments found</h3>
              <p className="text-gray-500 mb-4">You haven't completed any assessments yet.</p>
              <button
                onClick={() => navigate('/free-assessment')}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                Take M-CHAT Assessment
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {assessments.map((assessment) => (
                <div key={assessment.id} className="bg-white rounded-lg shadow overflow-hidden border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <div className="mb-4 sm:mb-0">
                        <h3 className="text-lg font-medium text-gray-900 flex items-center">
                          <FaFilePdf className="text-red-500 mr-2" />
                          {assessment.childName || 'Untitled Assessment'}
                        </h3>
                        <div className="mt-1 flex flex-wrap items-center text-sm text-gray-500">
                          <span className="flex items-center mr-4">
                            <FaCalendarAlt className="mr-1" />
                            {formatDate(assessment.createdAt)}
                          </span>
                          {assessment.childsAgeInMonths && (
                            <span className="flex items-center">
                              <FaChild className="mr-1" />
                              {assessment.childsAgeInMonths} months
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        {assessment.riskLevel && (
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRiskLevelClass(assessment.riskLevel)}`}>
                            {assessment.riskLevel}
                          </span>
                        )}
                        <button
                          onClick={() => navigate(`/app/report/${assessment.id}`)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          View Report
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
